import styles from "./AskAnything.module.scss";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import Select from "@/components/shared/select";
import {
  DateRange,
  formatPresetValue,
} from "@/components/shared/date-range-picker-v2/DateRange.model";
import { fieldToLabelMap } from "@/v2/features/explore/filters/filter.utils";
import { NewQuackLogo } from "@/v2/assets/newQuackLogo";
import {
  AskAnythingSessionProvider,
  AskAnythingChatSession,
  useAskAnythingChat,
} from "./chat/AskAnythingChatWrapper";
import { AskAnythingChat } from "./chat/AskAnythingChat";
import { Text, IconButton } from "@radix-ui/themes";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
} from "react";
import improveChatLoader from "@/lottie/blood_cell_morph_loader.json";
import Lottie, { Options as LottieOptions, LottieProps } from "react-lottie";
import { CommentAdd01Icon } from "@/v2/assets/icons/CommentAdd01Icon";
import { ArrowExpand01Icon } from "@/v2/assets/icons/ArrowExpand01Icon";
import { Dialog } from "@/components/shared/dialog";
import { exploreColumnTypeFormatters } from "@/v2/entities/explore/formatters/explore-formatters";
import { ExploreColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { FieldFilter } from "@/v2/components/filter-menu/FieldFilter.model";

const LottieDisplay = Lottie as ComponentType<LottieProps>;

interface AskAnythingPanelProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  source: ExploreInteractionType;
  filter: Filter;
  dateRange?: DateRange;
  filters?: ExploreFiltersValue;
}

interface AskAnythingPanelContentProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  dateRange?: DateRange;
  filters?: ExploreFiltersValue;
  source: ExploreInteractionType;
  filter: Filter;
}

const AskAnythingPanelContent = ({
  isOpen,
  onOpenChange,
  dateRange,
  filters,
  source,
  filter,
}: AskAnythingPanelContentProps) => {
  const { sessions, activeSessionId, switchSession, createNewSession } =
    useAskAnythingChat();
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset expanded state when panel opens to prevent duplicate rendering
  useEffect(() => {
    if (isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  const activeFilters = useMemo(() => {
    const items: Array<{ label: string; color: "blue" | "gray" }> = [];
    if (dateRange) {
      if (dateRange.type === "preset") {
        items.push({
          label: formatPresetValue(dateRange.value),
          color: "blue",
        });
      } else {
        items.push({ label: "Custom Range", color: "blue" });
      }
    }

    if (filters) {
      Object.entries(filters).forEach(([key, filter]) => {
        const f: FieldFilter | undefined = filter;
        const fieldLabel = fieldToLabelMap[key] || key;

        if (f && typeof f === "object" && "operator" in f) {
          if (f.operator === "isnull") {
            items.push({
              label: `No ${fieldLabel}`,
              color: "gray",
            });
          } else if (f.operator === "isnotnull") {
            items.push({
              label: `Has ${fieldLabel}`,
              color: "gray",
            });
          } else if ("value" in f) {
            const val = f.value;
            const values = Array.isArray(val) ? val : [val];
            const formatter =
              exploreColumnTypeFormatters[key as ExploreColumnType];

            values.forEach((v) => {
              let label = String(v);
              if (formatter) {
                try {
                  label = formatter.getText(String(v));
                } catch (e) {
                  // Fallback to value if formatter fails
                }
              }
              items.push({ label, color: "gray" });
            });
          } else {
            // Fallback for objects without value/operator known structure
            items.push({ label: fieldLabel, color: "gray" });
          }
        } else {
          // Fallback
          items.push({ label: fieldLabel, color: "gray" });
        }
      });
    }

    return items;
  }, [dateRange, filters]);

  const lottieOptions = useMemo<LottieOptions>(
    () => ({
      loop: true,
      autoplay: true,
      animationData: improveChatLoader,
    }),
    []
  );

  const selectOptions = useMemo(
    () =>
      sessions.map((session) => ({
        label: session.title,
        value: session.id,
      })),
    [sessions]
  );

  const handleSessionChange = useCallback(
    (value: string) => {
      switchSession(value);
    },
    [switchSession]
  );

  const handleNewSession = useCallback(() => {
    createNewSession();
  }, [createNewSession]);

  const handleExpandView = useCallback(() => {
    setIsExpanded(true);
    onOpenChange(false);
  }, [onOpenChange]);
  const handleCollapseView = useCallback(() => {
    setIsExpanded(false);
    onOpenChange(true);
  }, [onOpenChange]);
  const handleExpandedChange = useCallback(
    (open: boolean) => {
      setIsExpanded(open);
      if (!open) {
        onOpenChange(true);
      }
    },
    [onOpenChange]
  );

  const panelEmptyState = useMemo(
    () => (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>
          <LottieDisplay
            options={lottieOptions}
            height={32}
            width={32}
            isClickToPauseDisabled={true}
          />
        </div>
        <Text size="5" weight="bold">
          Ask your data anything
        </Text>
        <div className={styles.emptyStateTextWrapper}>
          <Text
            size="2"
            color="gray"
            align="center"
            className={styles.emptyStateText}
          >
            This chat uses the Explore filters that were active when it started.
          </Text>
        </div>
        <div className={styles.badges}>
          {activeFilters.map((badge, i) => (
            <div key={i} className={styles.filterBadge}>
              <Text size="1" weight="medium">
                {badge.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    ),
    [activeFilters, lottieOptions]
  );

  const expandedEmptyState = useMemo(
    () => (
      <div className={`${styles.emptyState} ${styles.emptyStateExpanded}`}>
        <div className={styles.emptyStateIcon}>
          <LottieDisplay
            options={lottieOptions}
            height={32}
            width={32}
            isClickToPauseDisabled={true}
          />
        </div>
        <Text
          size="5"
          weight="medium"
          className={styles.emptyStateTitleExpanded}
        >
          Ask your data anything
        </Text>
        <div
          className={`${styles.emptyStateTextWrapper} ${styles.emptyStateTextWrapperExpanded}`}
        >
          <Text
            size="2"
            color="gray"
            align="center"
            className={`${styles.emptyStateText} ${styles.emptyStateTextExpanded}`}
          >
            Ask questions about the data you're currently exploring. This chat
            uses your active filters.
          </Text>
        </div>
        <div className={styles.expandedBadges}>
          {activeFilters.map((badge, i) => (
            <div key={i} className={styles.filterBadge}>
              <Text size="1" weight="medium">
                {badge.label}
              </Text>
            </div>
          ))}
        </div>
      </div>
    ),
    [activeFilters, lottieOptions]
  );

  const renderHeader = useCallback(
    (variant: "panel" | "expanded") => {
      const onExpandClick =
        variant === "expanded" ? handleCollapseView : handleExpandView;

      return (
        <div
          className={`${styles.header} ${
            variant === "expanded" ? styles.expandedHeader : ""
          }`}
        >
          <div className={styles.headerLeft}>
            <div className={styles.logoWrapper}>
              <NewQuackLogo />
            </div>
            <Select
              value={activeSessionId}
              options={selectOptions}
              onChange={handleSessionChange}
              classes={{
                trigger: styles.selectTrigger,
                value:
                  variant === "expanded"
                    ? styles.selectValueExpanded
                    : styles.selectValue,
              }}
            />
          </div>
          <div className={styles.headerRight}>
            <IconButton
              variant="ghost"
              color="gray"
              className={styles.headerAction}
              onClick={handleNewSession}
            >
              <CommentAdd01Icon size={13} />
            </IconButton>
            <IconButton
              variant="ghost"
              color="gray"
              className={styles.headerAction}
              onClick={onExpandClick}
            >
              <ArrowExpand01Icon size={13} />
            </IconButton>
          </div>
        </div>
      );
    },
    [
      handleCollapseView,
      handleExpandView,
      handleNewSession,
      handleSessionChange,
      selectOptions,
      activeSessionId,
    ]
  );

  // We wrap ONLY the chat component with the keyed Session provider.
  // This ensures that when activeSessionId changes:
  // 1. The Header (renderHeader) DOES NOT UNMOUNT (no flicker).
  // 2. The AskAnythingChat unmounts and remounts with fresh state (clean switch).

  return (
    <>
      <div className={`${styles.container} ${isOpen ? styles.open : ""}`}>
        <div className={styles.window}>
          {renderHeader("panel")}
          <div className={styles.chatContainer}>
            <AskAnythingChatSession
              key={activeSessionId}
              source={source}
              filter={filter}
            >
              <AskAnythingChat emptyState={panelEmptyState} variant="default" />
            </AskAnythingChatSession>
          </div>
        </div>
      </div>

      <Dialog open={isExpanded} onOpenChange={handleExpandedChange}>
        <Dialog.Content className={styles.expandedDialog} size="large">
          <div className={`${styles.window} ${styles.expandedWindow}`}>
            {renderHeader("expanded")}
            <div
              className={`${styles.chatContainer} ${styles.chatContainerExpanded}`}
            >
              <AskAnythingChatSession
                key={activeSessionId}
                source={source}
                filter={filter}
              >
                <AskAnythingChat
                  emptyState={expandedEmptyState}
                  variant="expanded"
                />
              </AskAnythingChatSession>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

export const AskAnythingPanel = ({
  isOpen,
  onOpenChange,
  source,
  filter,
  dateRange,
  filters,
}: AskAnythingPanelProps) => {
  return (
    <AskAnythingSessionProvider source={source}>
      <AskAnythingPanelContent
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        dateRange={dateRange}
        filters={filters}
        source={source}
        filter={filter}
      />
    </AskAnythingSessionProvider>
  );
};
